import React, { useEffect, useState } from "react";
import moment from "moment";
import api from "../../api";
import { notify } from "../../utils/toast";
import { Pagination, Stack } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix leaflet broken marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LIMIT = 10;

export const Enquiries = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [location, setLocation] = useState(null);
  // location shape: { label, lat, lon } | { label } | null

  const getData = async (pageVal = page, status = statusFilter) => {
    try {
      setLoader(true);
      const response = await api.getEnquiries({
        page: pageVal,
        limit: LIMIT,
        status,
      });
      if (response?.success) {
        setData(response?.enquiries || []);
        setTotal(response?.total || 0);
        setTotalPages(response?.totalPages || 1);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData(1, "");
  }, []);

  const handlePageChange = (e, value) => {
    setPage(value);
    getData(value, statusFilter);
  };

  const handleStatusFilter = (e) => {
    const val = e.target.value;
    setStatusFilter(val);
    setPage(1);
    getData(1, val);
  };

  const updateStatus = async (id, status) => {
    try {
      const response = await api.updateEnquiry(id, { status });
      if (response?.success) {
        getData(page, statusFilter);
        if (selected?._id === id) setSelected((prev) => ({ ...prev, status }));
      }
    } catch {
      notify.error("Something went wrong!");
    }
  };

  const deleteEnquiry = async (id) => {
    try {
      const response = await api.deleteEnquiry(id);
      if (response?.success) {
        notify.success("Enquiry deleted!");
        if (selected?._id === id) {
          setSelected(null);
          setLocation(null);
        }
        getData(page, statusFilter);
      }
    } catch {
      notify.error("Something went wrong!");
    }
  };

  const fetchLocation = async (ip) => {
    setLocation({ label: "Fetching location..." });
    try {
      if (!ip || ip === "::1" || ip === "127.0.0.1") {
        setLocation({ label: "Localhost (dev environment)" });
        return;
      }
      const res = await fetch(
        `http://ip-api.com/json/${ip}?fields=status,city,district,regionName,country,lat,lon`,
      );
      const d = await res.json();
      if (d.status === "success") {
        const parts = [d.district, d.city, d.regionName, d.country].filter(
          Boolean,
        );
        setLocation({ label: parts.join(", "), lat: d.lat, lon: d.lon });
      } else {
        setLocation({ label: "Location unavailable" });
      }
    } catch {
      setLocation({ label: "Unable to fetch location" });
    }
  };

  const openEnquiry = (item) => {
    setSelected(item);
    setLocation(null);
    fetchLocation(item.ip);
    if (item.status === "new") updateStatus(item._id, "read");
  };

  const closePanel = () => {
    setSelected(null);
    setLocation(null);
  };

  const statusBadge = (status) => {
    const map = {
      new: "bg-danger",
      read: "bg-warning text-dark",
      replied: "bg-success",
    };
    return (
      <span className={`badge ${map[status] || "bg-secondary"} text-uppercase`}>
        {status}
      </span>
    );
  };

  return (
    <div className="DashboardMain">
      <div className="row">
        <div className="col">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">Enquiries ({total})</h3>
            <select
              className="form-control"
              style={{ width: "160px" }}
              value={statusFilter}
              onChange={handleStatusFilter}
            >
              <option value="">All</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
            </select>
          </div>

          {/* Table + Detail Panel */}
          <div className="d-flex gap-3" style={{ alignItems: "flex-start" }}>
            {/* Table */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {loader ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status" />
                </div>
              ) : (
                <div className="TableMain mt-0">
                  <div className="table-responsive">
                    <table className="table table-hover drivers-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Service</th>
                          <th>Status</th>
                          <th>IP</th>
                          <th>Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.length > 0 ? (
                          data.map((item, key) => (
                            <tr
                              key={key}
                              style={{
                                cursor: "pointer",
                                background:
                                  selected?._id === item._id ? "#f0fdf4" : "",
                              }}
                              onClick={() => openEnquiry(item)}
                            >
                              <td>
                                <div className="table-text-dark fw-600">
                                  {item.name}
                                </div>
                                <small className="text-muted">
                                  {item.email}
                                </small>
                              </td>
                              <td>
                                <span className="table-text-dark">
                                  {item.phone}
                                </span>
                              </td>
                              <td>
                                <span
                                  className="table-text-dark"
                                  style={{ textTransform: "capitalize" }}
                                >
                                  {item.service}
                                </span>
                              </td>
                              <td>{statusBadge(item.status)}</td>
                              <td>
                                <span className="table-text-dark">
                                  {item?.ip}
                                </span>
                              </td>
                              <td>
                                <span className="table-text-dark">
                                  {moment(item.createdAt).format("DD MMM YY")}
                                </span>
                              </td>
                              <td>
                                <div
                                  className="delete p-2 ms-1 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteEnquiry(item._id);
                                  }}
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center py-4 text-muted"
                            >
                              No enquiries found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {data && data.length > 0 && total > LIMIT && (
                <div className="pagination-container">
                  <Stack spacing={2}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      sx={{
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "#10b981",
                          color: "white",
                          "&:hover": { backgroundColor: "#059669" },
                        },
                        "& .MuiPaginationItem-root": {
                          "&:hover": {
                            backgroundColor: "#d1fae5",
                            color: "#059669",
                          },
                        },
                      }}
                    />
                  </Stack>
                </div>
              )}
            </div>

            {/* Detail Panel */}
            {selected && (
              <div
                style={{
                  width: "360px",
                  flexShrink: 0,
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  background: "#fff",
                  position: "sticky",
                  top: "20px",
                  overflow: "hidden",
                }}
              >
                {/* Panel Header */}
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h6 className="mb-0 fw-bold">Enquiry Detail</h6>
                  <button className="btn btn-sm btn-light" onClick={closePanel}>
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>

                {/* Map */}
                <div
                  style={{
                    height: "180px",
                    background: "#f5f5f5",
                    position: "relative",
                  }}
                >
                  {!location ? (
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        className="spinner-border spinner-border-sm text-secondary"
                        role="status"
                      />
                    </div>
                  ) : location.lat ? (
                    <MapContainer
                      key={`${location.lat}-${location.lon}`}
                      center={[location.lat, location.lon]}
                      zoom={13}
                      style={{ height: "100%", width: "100%" }}
                      scrollWheelZoom={false}
                      zoomControl={true}
                      attributionControl={false}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <Marker position={[location.lat, location.lon]}>
                        <Popup>{location.label}</Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#aaa",
                      }}
                    >
                      <i
                        className="fa-solid fa-map-location-dot"
                        style={{ fontSize: 28, marginBottom: 8 }}
                      ></i>
                      <span style={{ fontSize: 12 }}>Map unavailable</span>
                    </div>
                  )}

                  {/* Location label overlay */}
                  {location?.label && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        right: 8,
                        zIndex: 999,
                        background: "rgba(0,0,0,0.55)",
                        color: "#fff",
                        borderRadius: 6,
                        padding: "4px 10px",
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <i className="fa-solid fa-location-dot text-danger"></i>
                      {location.label}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div
                  style={{
                    padding: "16px 20px",
                    maxHeight: "420px",
                    overflowY: "auto",
                  }}
                >
                  <div className="mb-2">
                    <small className="text-muted d-block">Name</small>
                    <strong>{selected.name}</strong>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">Email</small>
                    <a href={`mailto:${selected.email}`}>{selected.email}</a>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">Phone</small>
                    <a href={`tel:${selected.phone}`}>{selected.phone}</a>
                  </div>
                  {selected.institute && (
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        Institute / Company
                      </small>
                      <strong>{selected.institute}</strong>
                    </div>
                  )}
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      Service Interested
                    </small>
                    <strong style={{ textTransform: "capitalize" }}>
                      {selected.service}
                    </strong>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Message</small>
                    <p
                      style={{
                        whiteSpace: "pre-wrap",
                        marginBottom: 0,
                        fontSize: 14,
                        lineHeight: 1.6,
                      }}
                    >
                      {selected.message}
                    </p>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Received</small>
                    <span>
                      {moment(selected.createdAt).format(
                        "DD MMM YYYY, hh:mm A",
                      )}
                    </span>
                  </div>

                  {/* Status buttons */}
                  <div className="mb-2">
                    <small className="text-muted d-block mb-1">
                      Update Status
                    </small>
                    <div className="d-flex gap-2 flex-wrap">
                      {["new", "read", "replied"].map((s) => (
                        <button
                          key={s}
                          className={`btn btn-sm ${selected.status === s ? "btn-success" : "btn-outline-secondary"}`}
                          style={{ textTransform: "capitalize" }}
                          onClick={() => {
                            updateStatus(selected._id, s);
                            setSelected({ ...selected, status: s });
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="mt-3 d-flex gap-2">
                    <a
                      href={`mailto:${selected.email}`}
                      className="btn btn-sm btn-outline-primary w-100"
                    >
                      <i className="fa-solid fa-envelope me-1"></i>Reply via
                      Email
                    </a>
                    <a
                      href={`tel:${selected.phone}`}
                      className="btn btn-sm btn-outline-success w-100"
                    >
                      <i className="fa-solid fa-phone me-1"></i>Call
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
